import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { BaseError } from 'helpers/BaseError';

import alternateKeys from 'hooks/alternates/_queryKeys';
import formulaKeys from 'hooks/formulas/_queryKeys';
import useAnalytics from 'hooks/helper/useAnalytics';
import useGetCurrentMessageContent from 'hooks/message-content/useGetCurrentMessageContent';
import personalizationKeys from 'hooks/personalization/_queryKeys';

import { VariantCreate } from 'models/variant.model';

import API from '../../services/api';

const useVariantCreateMutation = (
    componentId: string,
    formulaId: number,
    label: any,
) => {
    const queryClient = useQueryClient();
    const { currentMessageContentId } = useGetCurrentMessageContent(formulaId);
    const queryKey = alternateKeys.getVariantsByComponentId(componentId);

    const { track } = useAnalytics();

    return useMutation<AxiosResponse, BaseError, VariantCreate>({
        mutationFn: API.createVariantV1,
        onMutate: async (newVariant) => {
            await queryClient.cancelQueries({
                queryKey: queryKey,
            });
            const previousVariants = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, (old: any = []) => [
                {
                    content: newVariant.content,
                    id: 'optimistic',
                    label: label ? label : undefined,
                },
                ...old,
            ]);
            return {
                previousVariants,
            };
        },
        onError: (err, newAlternate, context: any) => {
            queryClient.setQueryData(queryKey, context.previousVariants);
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: alternateKeys.getVariantsByMessageContentId(
                    currentMessageContentId as string,
                ),
            });
            queryClient.invalidateQueries({
                queryKey: personalizationKeys.personalizationMap,
            });
            queryClient.invalidateQueries({
                queryKey: formulaKeys.formulaMessages(
                    formulaId,
                    currentMessageContentId,
                ),
            });
            queryClient.invalidateQueries({
                queryKey: formulaKeys.formulaMessageCountUpdate(formulaId),
            });
            queryClient.invalidateQueries({
                queryKey: personalizationKeys.getHeroRecommendations(),
            });
            queryClient.invalidateQueries({
                queryKey: alternateKeys.getVariantsByComponentId(componentId),
            });
        },
        onSuccess: () => {
            track('alternate_created', {
                formula_id: formulaId,
            });
        },
    });
};

export default useVariantCreateMutation;
